/**
 * Unified Breakpoints - Single Source of Truth
 * 
 * Use these for all v2 components to ensure JS and CSS stay in sync.
 * Legacy components should continue using their existing breakpoints.
 * 
 * Usage:
 * - JS: import { BREAKPOINTS } from 'styles/breakpoints'
 * - SCSS: @import 'styles/breakpoints' (uses CSS custom properties)
 * 
 * Breakpoint strategy (mobile-first):
 * - mobile: 0 - 767px
 * - tablet-small (iPad Mini/iPad portrait): 768px - 819px
 * - tablet (iPad Air portrait): 820px - 1023px
 * - tablet-large (iPad Pro 12.9" portrait, iPad landscape): 1024px - 1179px
 * - desktop: 1180px+
 * 
 * Device reference:
 * - iPad Mini portrait: 768px
 * - iPad (standard) portrait: 768px
 * - iPad Air portrait: 820px
 * - iPad Pro 11" portrait: 834px
 * - iPad Mini/iPad landscape: 1024px
 * - iPad Pro 12.9" portrait: 1024px
 * - iPad Air landscape: 1180px
 * - iPad Pro 11" landscape: 1194px
 * - iPad Pro 12.9" landscape: 1366px
 */

export const BREAKPOINTS = {
  /** Small iPhone (< 376px) */
  IPHONE_SMALL: 376,
  /** Small mobile devices (< 480px) */
  SMALL_MOBILE: 480,
  /** Standard mobile devices (< 768px) */
  MOBILE: 768,
  /** iPad Mini / iPad portrait (768px - 819px) */
  TABLET_SMALL: 768,
  /** iPad Air portrait / iPad Pro 11" portrait (820px - 1023px) */
  TABLET: 820,
  /** iPad landscape / iPad Pro 12.9" portrait (1024px - 1179px) */
  TABLET_LARGE: 1024,
  /** Desktop devices (1180px+) - iPad Air landscape and larger */
  DESKTOP: 1180,
  /** Large desktop devices (1440px+) */
  LARGE_DESKTOP: 1440,
  /** Extra large desktop devices (1920px+) */
  XL_DESKTOP: 1920,
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Media query helpers for JS (e.g., window.matchMedia)
 */
export const MEDIA_QUERIES = {
  /** Small iPhone only: < 376px */
  IPHONE_SMALL_ONLY: `(max-width: ${BREAKPOINTS.IPHONE_SMALL - 1}px)`,
  /** Mobile only: < 768px */
  MOBILE_ONLY: `(max-width: ${BREAKPOINTS.MOBILE - 1}px)`,
  /** Tablet small and up (iPad Mini/iPad portrait): >= 768px */
  TABLET_SMALL_UP: `(min-width: ${BREAKPOINTS.TABLET_SMALL}px)`,
  /** Tablet and up (iPad Air portrait): >= 820px */
  TABLET_UP: `(min-width: ${BREAKPOINTS.TABLET}px)`,
  /** Tablet large and up (iPad landscape): >= 1024px */
  TABLET_LARGE_UP: `(min-width: ${BREAKPOINTS.TABLET_LARGE}px)`,
  /** Desktop and up: >= 1180px */
  DESKTOP_UP: `(min-width: ${BREAKPOINTS.DESKTOP}px)`,
  /** Large desktop and up: >= 1440px */
  LARGE_DESKTOP_UP: `(min-width: ${BREAKPOINTS.LARGE_DESKTOP}px)`,
} as const;

/**
 * Helper to check if viewport is tablet or larger (shows sidebar)
 * Use this for components that need sidebar visibility at tablet widths
 */
export const isTabletOrLarger = (width: number): boolean => width >= BREAKPOINTS.TABLET_SMALL;

/**
 * Helper to check if viewport is desktop or larger
 */
export const isDesktopOrLarger = (width: number): boolean => width >= BREAKPOINTS.DESKTOP;
